<div class="eligibility-check-container">
  <div>
    <h1 class="eligibility-check-header">
      {{ "REGISTER.ELIGIBILITY_CHECK.HEADER" | translate }}
    </h1>
    <p class="eligibility-check-step-explanation">
      {{ "REGISTER.ELIGIBILITY_CHECK.EXPLANATION" | translate }}
    </p>
  </div>

  @if (state().isUsingSameEmail) {
    <c-banner
      #sameEmailWarning
      tabindex="0"
      [style]="'warning'"
      [showIcon]="false"
    >
      <span>{{
        "REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL_MATCHES_CURRENT_ACCOUNT"
          | translate
      }}</span>
    </c-banner>
  }

  @if (state().hasActiveAccountWarning) {
    <c-banner
      #activeAccountWarning
      tabindex="0"
      [style]="'warning'"
      [showIcon]="false"
    >
      <span class="eligibility-check-found-active-account">{{
        "REGISTER.ELIGIBILITY_CHECK.FOUND_ACTIVE_ACCOUNT" | translate
      }}</span
      >{{ "REGISTER.ELIGIBILITY_CHECK.PLEASE" | translate
      }}<button
        cari-button
        data-size="small"
        data-shape="link"
        data-type="action"
        type="button"
        (click)="handleLoginClick()"
        class="eligibility-check-active-account-warning-link-button"
      >
        {{ "REGISTER.ELIGIBILITY_CHECK.LOG_IN" | translate }}</button
      >{{ "REGISTER.ELIGIBILITY_CHECK.TO_CONTINUE_OR" | translate
      }}<button
        cari-button
        data-size="small"
        data-shape="link"
        data-type="action"
        type="button"
        (click)="handleResetPasswordClick()"
        class="eligibility-check-active-account-warning-link-button"
      >
        {{ "REGISTER.ELIGIBILITY_CHECK.RESET_PASSWORD" | translate }}</button
      >{{ "REGISTER.ELIGIBILITY_CHECK.IF_HAVING_TROUBLE" | translate }}
    </c-banner>
  }

  @if (state().hasEligibilityError) {
    <c-banner
      #eligibilityWarning
      tabindex="0"
      [style]="'warning'"
      iconPositionX="top"
    >
      <div class="banner-message-container">
        <span class="banner-message__message">
          <span class="banner-message__title">
            {{ "REGISTER.ELIGIBILITY_CHECK.FAIL_TITLE" | translate }}
          </span>
          @if (workEmailCheckFailed && personalInfoCheckFailed) {
            {{
              "REGISTER.ELIGIBILITY_CHECK.ALL_FAIL_MESSAGE_PART_1" | translate
            }}
            <a class="banner-message__link" href="mailto:support@cariloop.com">
              {{
                "REGISTER.ELIGIBILITY_CHECK.ALL_FAIL_MESSAGE_PART_2" | translate
              }}
            </a>
            {{
              "REGISTER.ELIGIBILITY_CHECK.ALL_FAIL_MESSAGE_PART_3" | translate
            }}
          } @else {
            {{
              "REGISTER.ELIGIBILITY_CHECK.METHOD_FAIL_MESSAGE_PART_1"
                | translate
            }}
            @if (workEmailCheckFailed) {
              <button
                c-link-button
                variant="small"
                class="link-button"
                (click)="
                  selectEligibilityCheckMethod($event, 'personalInformation')
                "
              >
                {{
                  "REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL_FAIL_MESSAGE"
                    | translate
                }}
              </button>
            }
            @if (personalInfoCheckFailed) {
              <button
                c-link-button
                variant="small"
                class="link-button"
                (click)="selectEligibilityCheckMethod($event, 'workEmail')"
              >
                {{
                  "REGISTER.ELIGIBILITY_CHECK.PERSONAL_INFORMATION_FAIL_MESSAGE"
                    | translate
                }}
              </button>
            }
          }
        </span>
      </div>
    </c-banner>
  }

  <div class="eligibility-check-form-container">
    @if (state().hasServerSideError) {
      <c-banner
        #serverSideError
        tabindex="0"
        [style]="'error'"
        message="{{ 'REGISTER.GENERIC_FORM.ERRORS.GENERIC' | translate }}"
      />
    }

    <form
      (ngSubmit)="handleSubmit()"
      [formGroup]="eligibilityCheckForm"
      id="eligibility-check-form"
      class="eligibility-check-form"
    >
      <div>
        <label
          class="eligibility-check-verification-method-label"
          id="verification-method-label"
        >
          {{
            "REGISTER.ELIGIBILITY_CHECK.VERIFICATION_METHOD_LABEL" | translate
          }}
        </label>
        <p
          id="verification-method-radio-group-description"
          class="screen-reader-only"
        >
          {{
            "REGISTER.ELIGIBILITY_CHECK.VERIFICATION_METHOD_SELECTION"
              | translate
          }}
          {{
            "REGISTER.ELIGIBILITY_CHECK.PREFERRED_VERIFICATION_METHOD"
              | translate
          }}
        </p>
        <mat-radio-group
          [disabled]="state().isSubmitting"
          formControlName="method"
          aria-labelledby="verification-method-label"
          aria-describedby="verification-method-radio-group-description"
          [attr.aria-controls]="'verification-fields'"
        >
          <mat-radio-button
            checked="true"
            class="eligibility-check-radio"
            value="workEmail"
            [aria-label]="'REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL' | translate"
          >
            <span aria-hidden="true">
              {{ "REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL" | translate }}
            </span>
          </mat-radio-button>
          <mat-radio-button
            class="eligibility-check-radio"
            value="personalInformation"
          >
            {{
              "REGISTER.ELIGIBILITY_CHECK.PERSONAL_INFORMATION.OPTION"
                | translate
            }}
          </mat-radio-button>
        </mat-radio-group>
      </div>

      <fieldset id="verification-fields">
        @if ((selectedMethod$ | async) === "personalInformation") {
          <legend class="screen-reader-only">
            {{
              "REGISTER.ELIGIBILITY_CHECK.PERSONAL_INFORMATION_FIELDSET_LEGEND"
                | translate
            }}
          </legend>
        }

        @if ((selectedMethod$ | async) === "workEmail") {
          <div formGroupName="workEmailGroup">
            <c-input-text
              autocomplete="work email"
              [disabled]="state().isSubmitting"
              class="eligibility-check-email-input"
              formControlName="workEmail"
              label="{{ 'REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL' | translate }}"
              type="email"
              ariaDescribedBy="work-email-input-hint"
            >
              @if (
                eligibilityCheckForm.get("workEmailGroup.workEmail")?.errors
                  ?.required !== undefined
              ) {
                {{
                  "REGISTER.ELIGIBILITY_CHECK.REQUIRED_EMAIL_ADDRESS"
                    | translate
                }}
              }
              @if (
                eligibilityCheckForm.get("workEmailGroup.workEmail")?.errors
                  ?.pattern !== undefined
              ) {
                {{
                  "REGISTER.ELIGIBILITY_CHECK.INVALID_EMAIL_ADDRESS" | translate
                }}
              }
            </c-input-text>
            <p id="work-email-input-hint" class="work-email-info">
              {{ "REGISTER.ELIGIBILITY_CHECK.WORK_EMAIL_INFO" | translate }}
            </p>
          </div>
        }

        @if ((selectedMethod$ | async) === "personalInformation") {
          <div formGroupName="personalEmailGroup" class="personal-info-form">
            <div class="full-name">
              <c-input-text
                autocomplete="given-name"
                class="name-input"
                name="firstName"
                type="text"
                [label]="'REGISTER.GENERIC_FORM.FIRST_NAME' | translate"
                formControlName="firstName"
              >
                @if (
                  eligibilityCheckForm.get("personalEmailGroup.firstName")
                    ?.errors?.required !== undefined &&
                  eligibilityCheckForm.get("personalEmailGroup.firstName")
                    ?.value === ""
                ) {
                  <p>
                    {{ "REGISTER.GENERIC_FORM.ERRORS.FIRST_NAME" | translate }}
                  </p>
                }
              </c-input-text>
              <c-input-text
                autocomplete="family-name"
                class="name-input"
                name="lastName"
                type="text"
                [label]="'REGISTER.GENERIC_FORM.LAST_NAME' | translate"
                formControlName="lastName"
              >
                @if (
                  eligibilityCheckForm.get("personalEmailGroup.lastName")
                    ?.errors?.required !== undefined &&
                  eligibilityCheckForm.get("personalEmailGroup.lastName")
                    ?.value === ""
                ) {
                  <p>
                    {{ "REGISTER.GENERIC_FORM.ERRORS.LAST_NAME" | translate }}
                  </p>
                }
              </c-input-text>
            </div>
            <cari-input-container>
              <label id="zipCode-label" cari-label for="zip-code-input"
                >{{
                  "REGISTER.ELIGIBILITY_CHECK.PERSONAL_INFORMATION.ZIP_CODE"
                    | translate
                }}
              </label>
              <input
                cari-input
                #zipCodeInput
                autocomplete="postal-code"
                id="zip-code-input"
                class="name-input"
                name="zip-code-input"
                type="text"
                inputmode="text"
                aria-required="true"
                aria-describedby="zip-code-error"
                formControlName="zipCode"
                [aria-invalid]="
                  eligibilityCheckForm.get('personalEmailGroup.zipCode')
                    ?.errors &&
                  eligibilityCheckForm.get('personalEmailGroup.zipCode')
                    ?.touched
                "
              />
              @if (
                eligibilityCheckForm.get("personalEmailGroup.zipCode")
                  ?.errors &&
                eligibilityCheckForm.get("personalEmailGroup.zipCode")?.touched
              ) {
                <cari-input-error id="zip-code-error">
                  @if (
                    eligibilityCheckForm.get("personalEmailGroup.zipCode")
                      .errors?.required
                  ) {
                    <span>
                      {{
                        "REGISTER.ELIGIBILITY_CHECK.ERRORS.ZIP_CODE_REQUIRED"
                          | translate
                      }}
                    </span>
                  }
                  @if (
                    eligibilityCheckForm.get("personalEmailGroup.zipCode")
                      .errors?.pattern &&
                    !eligibilityCheckForm.get("personalEmailGroup.zipCode")
                      .errors?.required
                  ) {
                    <span>
                      {{
                        "REGISTER.ELIGIBILITY_CHECK.ERRORS.ZIP_CODE_INVALID"
                          | translate
                      }}
                    </span>
                  }
                </cari-input-error>
              }
            </cari-input-container>
            <div class="date-picker">
              <c-input-datepicker
                autoComplete="bday"
                [required]="hasSubmitted()"
                name="date"
                startView="month"
                xPosition="end"
                [language]="SupportedLanguage.EN"
                formControlName="dateOfBirth"
                [max]="today"
                [label]="
                  'REGISTER.ELIGIBILITY_CHECK.PERSONAL_INFORMATION.DOB'
                    | translate
                "
                ariaDescribedBy="date-of-birth-input-error"
              ></c-input-datepicker>
              <p id="date-of-birth-input-error" class="error">
                @if (
                  eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                    ?.errors?.required &&
                  (eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                    .touched ||
                    isDOBTouched) &&
                  !eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                    ?.errors?.matDatepickerParse
                ) {
                  {{
                    "REGISTER.ELIGIBILITY_CHECK.ERRORS.DOB_REQUIRED" | translate
                  }}
                }
                @if (
                  eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                    ?.errors?.matDatepickerParse &&
                  (eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                    .touched ||
                    isDOBTouched)
                ) {
                  {{
                    "REGISTER.ELIGIBILITY_CHECK.ERRORS.DOB_INVALID" | translate
                  }}
                }
              </p>
            </div>
          </div>
        }
      </fieldset>

      @if (state().isSubmitting) {
        <mat-spinner
          diameter="40"
          [attr.aria-label]="
            'REGISTER.ELIGIBILITY_CHECK.SPINNER_LABEL' | translate
          "
        />
      } @else {
        <button
          cari-button
          type="submit"
          data-shape="squared"
          data-type="neutral"
          data-style="fill"
          data-size="x-large"
          class="brand-submit-btn"
          [ngClass]="{ 'full-button': (isMobileWidth$ | async) }"
        >
          {{ "REGISTER.ELIGIBILITY_CHECK.CONTINUE" | translate }}
        </button>
      }
    </form>
  </div>
</div>
